// src/pages/orders/OrderDetail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar,
  Avatar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  ShoppingBag as ShoppingBagIcon, 
  Person as PersonIcon, 
  CalendarToday as CalendarTodayIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const docRef = doc(db, 'orders', orderId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const orderData = docSnap.data();
          setOrder({ id: docSnap.id, ...orderData });
          setStatus(orderData.status || 'pending');
          setNotes(orderData.adminNotes || '');
        } else {
          setError('No se encontró el pedido');
          console.error('Order not found');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        setError('Error al cargar el pedido');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError('');
    
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status,
        adminNotes: notes,
        updatedAt: Timestamp.now()
      });
      
      setOrder({
        ...order,
        status,
        adminNotes: notes,
        updatedAt: Timestamp.now()
      });
      
      setSuccess(true);
    } catch (error) {
      console.error('Error updating order:', error);
      setError('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    const date = timestamp instanceof Timestamp ? 
      timestamp.toDate() : 
      new Date(timestamp.seconds * 1000);
      
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'processing':
        return 'En proceso';
      case 'shipped':
        return 'Enviado';
      case 'delivered':
        return 'Entregado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status || 'Desconocido';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Typography>Pedido no encontrado</Typography>
        <Button onClick={() => navigate('/admin/orders')}>Volver a la lista de pedidos</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Detalle de Pedido #{order.id.substring(0, 6).toUpperCase()}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/orders')}
        >
          Volver
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Order Details */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información del Pedido
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ShoppingBagIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    ID: <Typography component="span" fontWeight="bold">{order.id}</Typography>
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    Fecha: {formatDate(order.createdAt)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ mr: 1 }}>
                    Estado:
                  </Typography>
                  <Chip 
                    label={getStatusText(order.status)} 
                    color={getStatusColor(order.status)}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    Cliente: {order.userName || 'No especificado'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    Email: {order.userEmail || 'No especificado'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    Teléfono: {order.userPhone || 'No especificado'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Dirección de Envío
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <HomeIcon sx={{ mr: 1, color: 'text.secondary', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1">
                    {order.shippingAddress?.fullName || 'No especificado'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {order.shippingAddress?.street || ''}{order.shippingAddress?.number ? `, ${order.shippingAddress.number}` : ''}
                    {order.shippingAddress?.floor ? `, ${order.shippingAddress.floor}` : ''}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {order.shippingAddress?.postalCode || ''} {order.shippingAddress?.city || ''}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {order.shippingAddress?.province || ''} {order.shippingAddress?.country || ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Productos
            </Typography>
            
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {(order.items || []).map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      {item.imagen ? (
                        <Avatar alt={item.nombre} src={item.imagen} variant="rounded" />
                      ) : (
                        <Avatar variant="rounded">
                          <ShoppingBagIcon />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2">
                          {item.nombre}
                          {item.talla && <Chip size="small" label={`Talla: ${item.talla}`} sx={{ ml: 1 }} />}
                          {item.color && <Chip size="small" label={`Color: ${item.color}`} sx={{ ml: 1 }} />}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                          >
                            Cantidad: {item.quantity}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {(item.precio * item.quantity).toFixed(2)} €
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < (order.items || []).length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
            
            <Box sx={{ bgcolor: 'background.paper', p: 2, mt: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">{order.subtotal?.toFixed(2) || '0.00'} €</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Gastos de envío:</Typography>
                <Typography variant="body1">{order.shippingCost?.toFixed(2) || '0.00'} €</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{order.total?.toFixed(2) || '0.00'} €</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Order Management */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Gestión del Pedido
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Estado del pedido</InputLabel>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  label="Estado del pedido"
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="processing">En proceso</MenuItem>
                  <MenuItem value="shipped">Enviado</MenuItem>
                  <MenuItem value="delivered">Entregado</MenuItem>
                  <MenuItem value="cancelled">Cancelado</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Notas de administración"
                multiline
                rows={4}
                value={notes}
                onChange={handleNotesChange}
                placeholder="Añadir notas privadas sobre este pedido..."
              />
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={saving}
            >
              Guardar cambios
            </Button>
          </Paper>
          
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Historial del Pedido
              </Typography>
              
              <Box sx={{ pl: 2, borderLeft: '1px solid #e0e0e0' }}>
                {/* Historial del pedido (simplificado) */}
                <Box sx={{ position: 'relative', pb: 2 }}>
                  <Box sx={{ position: 'absolute', left: -16, width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="subtitle2">Pedido creado</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(order.createdAt)}
                  </Typography>
                </Box>
                
                {order.updatedAt && (
                  <Box sx={{ position: 'relative', pb: 2 }}>
                    <Box sx={{ position: 'absolute', left: -16, width: 10, height: 10, borderRadius: '50%', bgcolor: 'info.main' }} />
                    <Typography variant="subtitle2">Estado actualizado a "{getStatusText(order.status)}"</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(order.updatedAt)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">
          Pedido actualizado correctamente
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetail;